import { z } from 'zod'
import { emailSchema } from '../validators/email.validator.js'
import { avatarAssetRefSchema } from '../helpers/asset-ref.js'
import { phoneSchema } from '../validators/phone.validator.js'
import { TENANT_MEMBER_ROLES } from '../enums/role.enum.js'

/**
 * Roles an organization admin may assign when inviting or editing a user. Never
 * SUPER_ADMIN — that's a platform-level role, not something a tenant grants. A
 * VENUE_ADMIN is assigned to specific venues (user edit dialog or venue Settings).
 *
 * The same people who belong to a tenant. One list, so a role added to the
 * tenant cannot be assigned without also being a member, or the reverse.
 */
export const ASSIGNABLE_ROLES = TENANT_MEMBER_ROLES

export const assignableRoleSchema = z.enum(ASSIGNABLE_ROLES)

export type AssignableRole = z.infer<typeof assignableRoleSchema>

/**
 * An order that names every assignable role.
 *
 * Dropping one fails to compile, so a role added to the schema cannot vanish
 * from a menu that lists roles through this.
 */
export function everyAssignableRole<const T extends readonly AssignableRole[]>(
  order: T & ([AssignableRole] extends [T[number]] ? T : never),
): T {
  return order
}

/** A new account password — used when accepting an invite or resetting. */
export const passwordSchema = z.string().min(8, 'Use at least 8 characters').max(200)

/**
 * Only staff (`VENUE_ADMIN`) hold a company role and venues. A role without
 * them reads as before roles: staff on the built-in Venue manager, with venues
 * assigned afterwards (docs/custom-roles.spec.md, "People and the guardrail").
 */
const STAFF_ONLY = { message: 'Only staff work under a company role or at venues.', path: ['tenantRoleId'] }

/** The venues an Assigned-venues role's person works at, as given when inviting or assigning. */
const venueIdsSchema = z.array(z.string().uuid()).max(500)

/** Invite a user into the caller's tenant; they set their own password on accept. */
export const inviteUserInputSchema = z
  .object({
    email: emailSchema,
    name: z.string().trim().min(1).max(200),
    role: assignableRoleSchema,
    /** For staff: the company role they work under. Left out, the built-in Venue manager. */
    tenantRoleId: z.string().uuid().optional(),
    /** For staff on an Assigned-venues role: the venues they work at. */
    venueIds: venueIdsSchema.optional(),
  })
  .strict()
  .refine((input) => input.role === 'VENUE_ADMIN' || (input.tenantRoleId === undefined && input.venueIds === undefined), STAFF_ONLY)

export type InviteUserInput = z.infer<typeof inviteUserInputSchema>

/**
 * Invite a platform SUPER_ADMIN (or a tenant ORGANIZATION_ADMIN when nested
 * under a tenant). Role and scope come from the route, not the body.
 */
export const inviteByEmailInputSchema = z
  .object({
    email: emailSchema,
    name: z.string().trim().min(1).max(200),
  })
  .strict()

export type InviteByEmailInput = z.infer<typeof inviteByEmailInputSchema>

/** Edit an existing user's name and/or role. `.strict()` rejects unknown keys. */
export const updateUserInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    role: assignableRoleSchema.optional(),
    /** Staff's company role: with `role` set to staff, or for someone already staff. */
    tenantRoleId: z.string().uuid().optional(),
  })
  .strict()
  .refine((input) => input.role === undefined || input.role === 'VENUE_ADMIN' || input.tenantRoleId === undefined, STAFF_ONLY)

export type UpdateUserInput = z.infer<typeof updateUserInputSchema>

/** Replace the set of venues a VENUE_ADMIN manages (full replace). */
export const setManagedVenuesInputSchema = z
  .object({
    venueIds: venueIdsSchema,
  })
  .strict()

export type SetManagedVenuesInput = z.infer<typeof setManagedVenuesInputSchema>

/** Accept an invitation: prove the emailed token and set the first password. */
export const acceptInvitationInputSchema = z
  .object({
    token: z.string().min(1),
    password: passwordSchema,
  })
  .strict()

export type AcceptInvitationInput = z.infer<typeof acceptInvitationInputSchema>

/** Ask for a password-reset email (public; never reveals whether the email exists). */
export const requestPasswordResetInputSchema = z
  .object({
    email: emailSchema,
  })
  .strict()

export type RequestPasswordResetInput = z.infer<typeof requestPasswordResetInputSchema>

/**
 * Ask for a sign-in link (public; never reveals whether the email exists).
 *
 * `redirectTo` is where the person was heading when they were bounced to the
 * login page. It is not validated here — `safeRedirectPath` reduces it on the
 * server, and anything it cannot vouch for becomes null rather than a refusal,
 * so this route keeps its promise to answer the same way to everything.
 */
export const requestMagicLinkInputSchema = z
  .object({
    email: emailSchema,
    redirectTo: z.string().max(512).optional(),
  })
  .strict()

export type RequestMagicLinkInput = z.infer<typeof requestMagicLinkInputSchema>

/** Spend a sign-in link. The token is the whole credential. */
export const magicLinkSignInInputSchema = z
  .object({
    token: z.string().min(1),
  })
  .strict()

export type MagicLinkSignInInput = z.infer<typeof magicLinkSignInInputSchema>

/** Complete a password reset with the emailed token and a new password. */
export const resetPasswordInputSchema = z
  .object({
    token: z.string().min(1),
    password: passwordSchema,
  })
  .strict()

export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>

/**
 * Ask whose account an invitation or password-reset link is for, so the page
 * that sets the password can show it (and a password manager can save the
 * password under it). The token goes in the body, not the address, to keep it
 * out of request logs.
 */
export const linkAccountInputSchema = z
  .object({
    token: z.string().min(1),
  })
  .strict()

export type LinkAccountInput = z.infer<typeof linkAccountInputSchema>

/** Change your own password while signed in (must prove the current one). */
export const changePasswordInputSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: passwordSchema,
  })
  .strict()

export type ChangePasswordInput = z.infer<typeof changePasswordInputSchema>

/** Edit your own profile: display name and/or avatar (null clears the avatar).
 * Persist a storage key from POST /uploads; external/data URLs also accepted. */
export const updateProfileInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    avatarUrl: avatarAssetRefSchema.nullable().optional(),
  })
  .strict()

export type UpdateProfileInput = z.infer<typeof updateProfileInputSchema>

/** Set or clear a client's phone. Null clears it. */
export const updateClientPhoneInputSchema = z.object({ phone: phoneSchema.nullable() }).strict()

export type UpdateClientPhoneInput = z.infer<typeof updateClientPhoneInputSchema>
